// src/api/streamUntilInit.ts
//
// Minimal SSE-until-init reader for the shared chat stream endpoint (plan
// 2026-07-28 platform assistant, step 4.1 — assistant → data-chat handoff).
//
// It POSTs a message to POST /chats/stream and resolves as soon as the `init`
// SSE event yields the new conversation id, then aborts the fetch. It does NOT
// hold the stream open for the agent's answer.
//
// Why aborting after `init` is safe: the backend registers the agent run as a
// DETACHED asyncio background task — `chat_routes.py::_register_task` (called
// ~chat_routes.py:3052), fed a task created via `asyncio.create_task(...)`
// (~chat_routes.py:3034) — BEFORE the `event_generator` streams the `init` event
// (~chat_routes.py:3056). The task is not tied to the request, so a client
// disconnect does not cancel it: the analysis runs to completion and persists
// on the conversation regardless. The user then opens that conversation to see
// the answer. (Line numbers drift with parallel edits — the symbols are stable.)
//
// The request mechanics mirror useAgentStream (endpoint, JSON +
// text/event-stream headers, SSE line framing), going through `apiFetch` for
// the tab-account header and 401 → refresh → retry. The BFF /stream proxy
// injects user_id + account_id from the JWT and only reads workspace_id +
// message from the body — so the body carries just the question, the active
// workspace public id and the routing mode (never a `surface`).

import { apiFetch } from "./client";

export interface StreamUntilInitPayload {
  /** The (self-contained) message to send as the conversation's first message. */
  message: string;
  /** Active workspace public id (ws_...). */
  workspace_id: string;
  /** Agent routing mode. The handoff uses normal auto-routing. */
  routing_mode?: "auto" | "pinned";
}

export interface StreamUntilInitResult {
  conversationId: string;
}

/** Parse one SSE frame; return the conversation id on `init`, throw on `error`. */
function readInitFrame(eventType: string, data: string): string | null {
  if (eventType === "error") {
    let message = "The chat stream reported an error.";
    try {
      const parsed = JSON.parse(data);
      if (typeof parsed?.message === "string" && parsed.message) {
        message = parsed.message;
      }
    } catch {
      /* keep the default message */
    }
    throw new Error(message);
  }
  if (eventType !== "init") return null;
  try {
    const parsed = JSON.parse(data);
    const id = parsed?.conversation_id;
    return typeof id === "string" && id ? id : null;
  } catch {
    return null;
  }
}

/**
 * Open a chat-stream conversation and resolve with its id as soon as the `init`
 * event arrives, aborting the stream immediately afterwards (the agent run
 * survives the disconnect — see file header). Throws if the stream errors,
 * fails to start, or ends before emitting `init`.
 */
export async function streamUntilInit(
  payload: StreamUntilInitPayload,
): Promise<StreamUntilInitResult> {
  const controller = new AbortController();

  let response: Response;
  try {
    response = await apiFetch("/chats/stream", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify({
        message: payload.message,
        workspace_id: payload.workspace_id,
        routing_mode: payload.routing_mode ?? "auto",
      }),
      signal: controller.signal,
    });
  } catch (e) {
    controller.abort();
    throw e instanceof Error ? e : new Error("Failed to start the chat stream.");
  }

  if (!response.ok) {
    controller.abort();
    throw new Error(`Chat stream failed to start (${response.status}).`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    controller.abort();
    throw new Error("Chat stream returned no response body.");
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";
  let currentData = "";

  // Mirrors useAgentStream's line framing: an event is `event: <type>` +
  // `data: <json>` lines terminated by a blank line; a single event can span
  // multiple network chunks, so currentEvent/currentData persist across reads.
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? ""; // keep the trailing incomplete line

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          // New event — flush any orphaned previous event first.
          if (currentEvent && currentData) {
            const id = readInitFrame(currentEvent, currentData);
            if (id) return { conversationId: id };
          }
          currentEvent = line.slice(7).trim();
          currentData = "";
        } else if (line.startsWith("data: ")) {
          currentData += line.slice(6);
        } else if (line === "" && currentEvent && currentData) {
          const id = readInitFrame(currentEvent, currentData);
          if (id) return { conversationId: id };
          currentEvent = "";
          currentData = "";
        }
      }
    }

    // Flush a final buffered event with no trailing blank line.
    if (currentEvent && currentData) {
      const id = readInitFrame(currentEvent, currentData);
      if (id) return { conversationId: id };
    }

    throw new Error("The chat stream ended before it started a conversation.");
  } finally {
    // Abort the fetch: we have what we need and the agent run continues server-side.
    controller.abort();
  }
}
